import { config } from "../config";
import { LAUNCH_TRADES, jobCategoryLabel } from "../types/enums";
import type {
  AvailableJobsResponse,
  AnonymizedReview,
  AvailabilityOverride,
  Bank,
  BankDetails,
  BanksResponse,
  Booking,
  BookingFilter,
  BookingsResponse,
  Contractor,
  ContractorService,
  Conversation,
  CreateQuoteRequest,
  DirectBookingSettings,
  ExtrasResponse,
  Job,
  JobsResponse,
  NearbyJobsResponse,
  PayoutsResponse,
  Quote,
  QuotesResponse,
  ServiceExtra,
  ServicesResponse,
  SubmitBankDetailsRequest,
  VerificationStatusResponse,
  WeeklyAvailability,
} from "../types/models";
import { ApiClient } from "./client";

const api = new ApiClient(config.contractorApi);

/* ---------------------------------------------------------------- onboarding */

export interface CompleteRegistrationRequest {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  businessName?: string;
  skills: string[];
  serviceAreaGeos: {
    name: string;
    placeId?: string;
    latitude: number;
    longitude: number;
    geohash?: string;
  }[];
  hourlyRate?: number;
  currency?: string;
  bio?: string;
}

/**
 * The sole record-creator. Registration itself happens client-side through the
 * Firebase SDK; this is what writes the /contractors document AND mints the
 * `userType: "contractor"` custom claim.
 *
 * Callers MUST force-refresh the ID token afterwards before any other
 * claim-gated call — see `idToken(true)` in lib/firebase.ts and issue #74.
 */
export function completeRegistration(body: CompleteRegistrationRequest): Promise<Contractor> {
  return api.post<Contractor>("/auth/complete", body);
}

export interface RedeemReferralResponse {
  referralId?: string;
  reviewId?: string;
  linked?: boolean;
  referrerId?: string;
  referrerName?: string;
}

export function redeemReferral(code: string): Promise<RedeemReferralResponse> {
  return api.post<RedeemReferralResponse>("/referrals/redeem", { code });
}

/* ------------------------------------------------------------------- profile */

export function getProfile(): Promise<Contractor> {
  return api.get<Contractor>("/profile");
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  businessName?: string;
  skills?: string[];
  serviceAreaGeos?: CompleteRegistrationRequest["serviceAreaGeos"];
  hourlyRate?: number;
  currency?: string;
  bio?: string;
  isAvailable?: boolean;
}

export function updateProfile(body: UpdateProfileRequest): Promise<Contractor> {
  return api.patch<Contractor>("/profile", body);
}

export function getReviews(limit = 50): Promise<{ reviews: AnonymizedReview[]; count: number }> {
  return api.get("/profile/reviews", { query: { limit } });
}

export function getVerificationStatus(): Promise<VerificationStatusResponse> {
  return api.get<VerificationStatusResponse>("/profile/verification-status");
}

/* ---------------------------------------------------------------------- bank */

export function listBanks(): Promise<Bank[]> {
  return api.get<BanksResponse>("/banks").then((r) => r.banks ?? []);
}

export function getBankDetails(): Promise<BankDetails> {
  return api.get<BankDetails>("/profile/bank-details");
}

/* --------------------------------------------------------------- trades */

export interface Trade {
  value: string;
  label: string;
  /** The SVG element BODY for this trade's glyph (e.g. `<path d="…" />`),
   *  `omitempty` server-side and therefore simply ABSENT for every trade the
   *  app already bundles a drawing for — a bundled glyph wins anyway (@ui/react
   *  TradeIcon). It exists so a genuinely new trade arrives with correct
   *  artwork and no deploy. UNTRUSTED: re-validated at the render by
   *  isSafeGlyphBody before it ever reaches innerHTML. */
  icon?: string;
}

interface TradesResponse {
  trades: Trade[];
  count: number;
}

/** Cold-start / offline fallback for `listTrades` below — LAUNCH_TRADES,
 *  never removed, only demoted. */
const BUNDLED: Trade[] = LAUNCH_TRADES.map((value) => ({ value, label: jobCategoryLabel(value) }));

let tradesPromise: Promise<Trade[]> | null = null;

/**
 * `GET /categories`, remote-or-bundled. An empty list, an error and a timeout
 * all resolve to `BUNDLED` — this never rejects, so every caller is a two-line
 * effect with no error branch and the fallback lives in exactly one place.
 * Memoized for the life of the page load; no TTL/invalidation, add one if a
 * relabel needs to land without a reload.
 */
export function listTrades(): Promise<Trade[]> {
  tradesPromise ??= api
    .get<TradesResponse>("/categories")
    .then((r) => (r.trades?.length ? r.trades : BUNDLED))
    .catch(() => BUNDLED);
  return tradesPromise;
}

/**
 * Answers **200, not 201**, and is idempotent once the Paystack subaccount
 * exists. On a validation failure it persists `bankVerificationStatus: failed`
 * BEFORE returning 422 — so a retry is safe, but the UI must re-read this
 * endpoint rather than trust whatever the form last held.
 */
export function submitBankDetails(body: SubmitBankDetailsRequest): Promise<BankDetails> {
  return api.post<BankDetails>("/profile/bank-details", body);
}

/* ---------------------------------------------------------------------- jobs */

/** `lat`/`lng` are display-only — a straight-line `distance` on each job's
 *  response, never a filter. Omitted entirely when no fix is known, so the
 *  server falls back to this contractor's stored profile location. */
export function getAvailableJobs(lat?: number, lng?: number): Promise<AvailableJobsResponse> {
  return api.get<AvailableJobsResponse>("/jobs/available", { query: { lat, lng } });
}

export function getMyJobs(status?: string): Promise<JobsResponse> {
  return api.get<JobsResponse>("/jobs/mine", { query: { status } });
}

export function getNearbyJobs(lat: number, lng: number, radiusKm = 25): Promise<NearbyJobsResponse> {
  return api.get<NearbyJobsResponse>("/jobs/nearby", { query: { lat, lng, radiusKm } });
}

export function getJob(jobId: string, lat?: number, lng?: number): Promise<Job> {
  return api.get<Job>(`/jobs/${encodeURIComponent(jobId)}`, { query: { lat, lng } });
}

export function startJob(jobId: string): Promise<Job> {
  return api.post<Job>(`/jobs/${encodeURIComponent(jobId)}/start`);
}

/** Moves the job to `PendingUserSignOff`, not `Completed`. Replaying is a 200. */
export function completeJob(jobId: string): Promise<Job> {
  return api.post<Job>(`/jobs/${encodeURIComponent(jobId)}/complete`);
}

/**
 * Hands a won marketplace job back to the marketplace — the exit Accept/Start/
 * Complete never gave a contractor, which is why the mode-switch blocker's
 * "complete or cancel these jobs first" was dead text on an unpaid job.
 *
 * Allowed only from `Accepted` or `Scheduled` with nothing held: once the
 * homeowner has paid, unwinding the hold is their cancellation to make, and the
 * server answers `409 FUNDS_HELD`. The returned job is unassigned and back at
 * `Quoted` or `Pending`, and this contractor's quote is rejected — so he can
 * quote it again if he changes his mind.
 */
export function withdrawFromJob(jobId: string): Promise<Job> {
  return api.post<Job>(`/jobs/${encodeURIComponent(jobId)}/withdraw`);
}

/**
 * Opens the thread between this contractor and the homeowner about one job —
 * the affordance that lets a question be asked BEFORE quoting. Until this
 * existed, `conversation.contractorId` was only ever written inside the
 * accept-quote transaction, so the job-details "message" link 403'd for every
 * contractor whose quote had not already been accepted.
 *
 * Idempotent by design: **201** the first time, **200** returning the same
 * thread on every repeat, so a double-click is not an error and the caller can
 * always navigate to the `conversationId` it gets back. That matters more than
 * it looks: `ApiClient` retries a 403 once with a force-refreshed token, so a
 * blocked contractor sends this twice.
 *
 * Deliberately NOT gated on a payout account — asking a question is not taking
 * on paid work. Quoting keeps that guard. The failures worth naming are
 * `409 WRONG_MODE` (bookable contractor, or a direct-booking job that already
 * has its own thread) and `403 CONTRACTOR_BLOCKED`; a job this contractor may
 * not see answers 404, never 403, so the endpoint cannot be used to probe
 * which job ids exist.
 */
export function createJobConversation(jobId: string): Promise<Conversation> {
  return api.post<Conversation>(`/jobs/${encodeURIComponent(jobId)}/conversation`);
}

/* -------------------------------------------------------------------- quotes */

export function createQuote(body: CreateQuoteRequest): Promise<Quote> {
  return api.post<Quote>("/quotes", body);
}

export function listQuotes(): Promise<Quote[]> {
  return api.get<QuotesResponse>("/quotes").then((r) => r.quotes ?? []);
}

/** Only the authoring contractor may read a quote; anyone else gets 403. */
export function getQuote(quoteId: string): Promise<Quote> {
  return api.get<Quote>(`/quotes/${encodeURIComponent(quoteId)}`);
}

/* ------------------------------------------------------------------- payouts */

export function listPayouts(): Promise<PayoutsResponse> {
  return api.get<PayoutsResponse>("/payouts");
}

/* ------------------------------------------------------------------ bookings */

export function listBookings(
  filter: BookingFilter,
  lat?: number,
  lng?: number,
): Promise<BookingsResponse> {
  return api.get<BookingsResponse>("/bookings", { query: { filter, lat, lng } });
}

export function getBooking(bookingId: string, lat?: number, lng?: number): Promise<Booking> {
  return api.get<Booking>(`/bookings/${encodeURIComponent(bookingId)}`, { query: { lat, lng } });
}

export function acceptBooking(bookingId: string): Promise<Booking> {
  return api.post<Booking>(`/bookings/${encodeURIComponent(bookingId)}/accept`);
}

/** Answers 204 with no body. */
export function declineBooking(bookingId: string, reason?: string): Promise<null> {
  return api.post<null>(`/bookings/${encodeURIComponent(bookingId)}/decline`, { reason });
}

/** Triggers a FULL refund when the booking was already `Paid`. */
export function cancelBooking(bookingId: string, reason?: string): Promise<Booking> {
  return api.post<Booking>(`/bookings/${encodeURIComponent(bookingId)}/cancel`, { reason });
}

/* --------------------------------------------------- direct-booking settings */

export function getDirectBookingSettings(): Promise<DirectBookingSettings> {
  return api.get<DirectBookingSettings>("/profile/direct-booking");
}

export interface UpdateDirectBookingRequest {
  bookingBufferMinutes?: number;
  bookingTimezone?: string;
  bookingLeadTimeHours?: number;
  bookingHorizonDays?: number;
  directBookingPolicyText?: string;
  cancellationPolicy?: {
    freeUntilHoursBefore?: number;
    partialRefundUntilHoursBefore?: number;
    partialRefundPercent?: number;
  };
}

/** Answers `409 WRONG_MODE` unless the contractor is in bookable mode. */
export function updateDirectBookingSettings(
  body: UpdateDirectBookingRequest,
): Promise<DirectBookingSettings> {
  return api.patch<DirectBookingSettings>("/profile/direct-booking", body);
}

/** Answers `409 MODE_SWITCH_BLOCKED` with a `blockers[]` list the UI shows verbatim. */
export function updateOperatingMode(
  operatingMode: "marketplace" | "bookable",
): Promise<DirectBookingSettings> {
  return api.patch<DirectBookingSettings>("/profile/operating-mode", { operatingMode });
}

/** Requires exactly seven days; windows are HH:MM, close > open, non-overlapping. */
export function updateWeeklyAvailability(body: WeeklyAvailability): Promise<WeeklyAvailability> {
  return api.put<WeeklyAvailability>("/profile/availability", body);
}

export function upsertAvailabilityOverride(
  date: string,
  body: Omit<AvailabilityOverride, "date">,
): Promise<AvailabilityOverride> {
  return api.put<AvailabilityOverride>(
    `/profile/availability/overrides/${encodeURIComponent(date)}`,
    body,
  );
}

/** Answers 204 with no body. */
export function deleteAvailabilityOverride(date: string): Promise<null> {
  return api.delete<null>(`/profile/availability/overrides/${encodeURIComponent(date)}`);
}

/** Bounded to today onward — otherwise every past closure lists forever. */
export function listAvailabilityOverrides(from: string): Promise<{ overrides: AvailabilityOverride[] }> {
  return api.get<{ overrides: AvailabilityOverride[] }>("/profile/availability/overrides", {
    query: { from },
  });
}

/* --------------------------------------------------------- service catalogue */

export function listServices(): Promise<ContractorService[]> {
  return api.get<ServicesResponse>("/profile/services").then((r) => r.services ?? []);
}

export type ServiceInput = Omit<ContractorService, "serviceId" | "createdAt" | "updatedAt">;

export function createService(body: ServiceInput): Promise<ContractorService> {
  return api.post<ContractorService>("/profile/services", body);
}

/** PATCH replaces the variants list wholesale — send every variant, not a delta. */
export function updateService(serviceId: string, body: ServiceInput): Promise<ContractorService> {
  return api.patch<ContractorService>(`/profile/services/${encodeURIComponent(serviceId)}`, body);
}

/** Hard delete. Answers 204. */
export function deleteService(serviceId: string): Promise<null> {
  return api.delete<null>(`/profile/services/${encodeURIComponent(serviceId)}`);
}

export function listExtras(): Promise<ServiceExtra[]> {
  return api.get<ExtrasResponse>("/profile/service-extras").then((r) => r.extras ?? []);
}

export type ExtraInput = Omit<ServiceExtra, "extraId">;

export function createExtra(body: ExtraInput): Promise<ServiceExtra> {
  return api.post<ServiceExtra>("/profile/service-extras", body);
}

export function updateExtra(extraId: string, body: ExtraInput): Promise<ServiceExtra> {
  return api.patch<ServiceExtra>(`/profile/service-extras/${encodeURIComponent(extraId)}`, body);
}

/** Answers 204. */
export function deleteExtra(extraId: string): Promise<null> {
  return api.delete<null>(`/profile/service-extras/${encodeURIComponent(extraId)}`);
}
